/**
 * ServiceForm — Odoo-style full-screen detail view (dv-* pattern)
 * Same aesthetic as ServerForm: TopBar → SmartBar → Tabbed Body + Sidebar
 * Tabs: Identificación | Red y Hosting | SLA y Organización | Dependencias
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  GlobeAltIcon,
  ServerStackIcon,
  ShieldExclamationIcon,
  LinkIcon,
  UserGroupIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  MapIcon,
  CubeIcon,
  CloudIcon,
  CloudArrowUpIcon,
  CommandLineIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import ContactsSmartButton from './ContactsSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import ServiceAuditTab from './ServiceAuditTab';
import ADTopologyView from './ADTopologyView';
import ServiceTopologyView, { TOPOLOGY_SERVICE_TYPES } from './ServiceTopologyView';
import ServiceAccessPointsTab from './ServiceAccessPointsTab';
import ServiceHostsSection from './ServiceHostsSection';
import ServiceOverviewMap from './ServiceOverviewMap';
import DocsInlineList from './DocsInlineList';
import ServiceConfigView, { visibleConfig } from './ServiceConfigView';
import FormCardsShell from './FormCardsShell';
import FormDirtyDock from './common/FormDirtyDock';
import FormCardsHead from './FormCardsHead';
import '../styles/DetailView.css';
import './ServerFormCards.css';
import './ServiceForm.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';

const AUDITABLE_TYPES = ['active_directory'];

// Icono por tipo de CI relacionado, para no mostrar una caja genérica cuando
// la relación apunta a un firewall, una controladora WiFi o una VM.
const REL_CI_ICONS = {
  service: '🧩', rack_element: '🖥️', virt_vm: '💻', printer: '🖨️',
  wifi_controller: '📡', wifi_access_point: '📶', firewall: '🛡️',
  router: '🌐', workstation: '🖥️', storage_lun: '💽', server_volume: '📂',
  isp_circuit: '🛰️', ipam_ip: '🔢', other: '📦',
  database_instance: '🗄️',
};

const REL_CI_LABELS = {
  service: 'Servicio',
  service_farm: 'Granja de servidores',
  database_instance: 'Instancia de base de datos',
  rack_element: 'Servidor físico', server: 'Servidor físico',
  storage: 'Almacenamiento', switch: 'Switch', pdu: 'PDU', panel: 'Panel de parcheo',
  virt_vm: 'Máquina virtual', virt_hypervisor: 'Hipervisor', virt_cluster: 'Clúster',
  storage_lun: 'LUN de almacenamiento', server_volume: 'Volumen',
  firewall: 'Firewall', router: 'Router', printer: 'Impresora',
  wifi_controller: 'Controlador WiFi', wifi_access_point: 'Punto de acceso',
  workstation: 'Estación de trabajo', isp_circuit: 'Circuito ISP',
  ipam_ip: 'Dirección IP', ipam_subnet: 'Subred',
  logical_component: 'Componente lógico',
};

// Relaciones que "consume" el servicio. Su inversa es el impacto: si otra
// relación no está aquí (por ejemplo `has_backup` o `monitors`), el servicio
// está proporcionando algo y el otro extremo depende de él.
const NEED_RELATION_NAMES = new Set([
  'depends_on', 'uses', 'runs_on', 'authenticated_by', 'authenticates_on',
  'protected_by', 'uses_lun', 'uses_storage', 'stores_on', 'replica_to',
  'exported_by', 'member_of', 'communicates_with', 'uses_db', 'uses_cache',
  'uses_queue', 'proxied_by', 'resolves_via', 'backed_up_by', 'monitored_by',
  'powered_by', 'syncs_with', 'ha_peer_with',
]);

// Etiquetas legibles de las instancias de BD (rol y método de copia). El backend
// las publica en /api/apps/meta; aquí van como respaldo para pintarlas directas.
const DB_ROLE_LABELS = {
  primary: 'Principal', replica: 'Réplica', standby: 'En espera (standby)',
  witness: 'Testigo', arbiter: 'Árbitro', standalone: 'Independiente',
};
const COPY_METHOD_LABELS = {
  native: 'réplica del propio motor',
  backup_restore: 'restaurada de un backup del SQL',
  vm_clone: 'clonada de la máquina',
};


const HOSTING_FALLBACK = [
  { value: 'bare_metal', label: 'Servidor físico' },
  { value: 'vm', label: 'Máquina virtual' },
  { value: 'docker', label: 'Docker / Contenedor' },
  { value: 'saas', label: 'SaaS externo' },
  { value: 'paas', label: 'PaaS' },
  { value: 'cloud_vm', label: 'VM en nube' },
  { value: 'unknown', label: 'Desconocido' },
];

// Explicación y icono de cada tipo. El texto es para personas, no para el modelo:
// nada de «rack_element» ni «virt_vm» en la pestaña de alojamiento.
const HOSTING_INFO = {
  bare_metal: {
    description: 'Corre directamente en un servidor del rack.',
    Icon: ServerStackIcon,
  },
  vm: {
    description: 'Corre dentro de una máquina virtual.',
    Icon: CubeIcon,
  },
  docker: {
    description: 'Contenedor sobre un servidor o una VM.',
    Icon: CommandLineIcon,
  },
  saas: {
    description: 'Servicio externo; no corre en tu infraestructura.',
    Icon: GlobeAltIcon,
  },
  paas: {
    description: 'Plataforma gestionada externa.',
    Icon: CloudArrowUpIcon,
  },
  cloud_vm: {
    description: 'Máquina virtual en AWS, Azure o GCP.',
    Icon: CloudIcon,
  },
  unknown: {
    description: 'Aún no está definido.',
    Icon: QuestionMarkCircleIcon,
  },
};

const EMPTY = {
  name: '', display_name: '', service_type: '', vendor: '', version: '',
  status: 'running', criticality: 'medium', hosting_type: 'vm',
  host_ci_type: '', host_ci_id: '', host_server_id: '', host_vm_id: '',
  ip_address_id: '', url: '', port: '', protocol: 'tcp',
  description: '', notes: '', tags: '', owner_team: '', support_contact: '',
  sla_tier: '', maintenance_window: '',
};

const statusColors = {
  running:     { bg: '#dcfce7', color: '#15803d', label: '● Running' },
  stopped:     { bg: '#fee2e2', color: '#991b1b', label: '● Stopped' },
  maintenance: { bg: '#fef9c3', color: '#854d0e', label: '● Mantenimiento' },
  degraded:    { bg: '#ffedd5', color: '#9a3412', label: '● Degradado' },
  unknown:     { bg: '#f1f5f9', color: '#64748b', label: '● Desconocido' },
};

const critColors = {
  critical: { bg: '#fee2e2', color: '#991b1b', label: 'Crítico' },
  high:     { bg: '#ffedd5', color: '#9a3412', label: 'Alto' },
  medium:   { bg: '#fef9c3', color: '#854d0e', label: 'Medio' },
  low:      { bg: '#dbeafe', color: '#1e40af', label: 'Bajo' },
};

function DependencyRow({ item, onOpenService, canDelete, onDelete }) {
  return (
    <div className="sf-dep-row">
      <span className="sf-dep-row-icon">{item.icon}</span>
      <div className="sf-dep-row-body">
        <div className="sf-dep-row-title">
          {item.serviceId ? (
            <button
              type="button"
              className="sf-dep-link"
              onClick={() => onOpenService?.(item.serviceId)}
              title="Abrir la ficha de este servicio"
            >
              {item.name} ↗
            </button>
          ) : (
            <span>{item.name}</span>
          )}
          <span className={`sf-dep-source${item.source === 'Diseñador' ? ' is-model' : ''}`}>{item.source}</span>
        </div>
        <div className="sf-dep-row-meta">
          <span>{item.relationLabel}</span>
          <span>·</span>
          <span>{item.kindLabel}</span>
          {item.port && <><span>·</span><span>puerto {item.port}</span></>}
          {item.notes && <span className="sf-dep-note">{item.notes}</span>}
        </div>
      </div>
      {canDelete && item.deletable && (
        <button
          type="button"
          className="sf-dep-remove"
          onClick={() => onDelete?.(item.depId)}
          title="Quitar esta dependencia"
        >
          <TrashIcon />
        </button>
      )}
    </div>
  );
}

export default function ServiceForm({ service, companyId, meta: metaProp, onClose, onSave, onOpenService, initialMode, variant = 'classic' }) {
  const isCards = variant === 'cards';
  /* La ficha se abre EN LECTURA (clic en la fila del listado); 'create' y 'edit'
     editan. Sin `initialMode` edita, como siempre. */
  const [editing, setEditing] = useState(initialMode ? initialMode !== 'view' : true);
  const isEditing = Boolean(service?.id);

  // ── Meta ──
  const [meta, setMeta] = useState(metaProp || { service_types: [], statuses: [], criticalities: [], hosting_types: [] });

  // ── Form ──
  const [form, setForm] = useState(EMPTY);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [errors, setErrors] = useState([]);
  const [apiError, setApiError] = useState('');

  // ── UI ──
  const [activeTab, setActiveTab] = useState('general');
  const [accessPointCount, setAccessPointCount] = useState(service?.access_point_count || 0);

  // ── Dependencies ──
  const [deps, setDeps] = useState({ outbound: [], inbound: [] });
  // Elementos del diseño (sd_service_members) con su rol: los mismos que se ven
  // como cajas en el Diseñador. Se pintan en la ficha para no tener que abrirlo.
  const [members, setMembers] = useState([]);
  // Relaciones tipadas creadas en el Diseñador (canvas): "se autentica en",
  // "proporciona"... Viven en otra tabla que las dependencias de arriba, así
  // que sin esto un enlace como VPN → RADIUS no se veía en la ficha.
  const [relations, setRelations] = useState({ out: [], in: [] });
  const [allSvcs, setAllSvcs] = useState([]);
  // Instancias de base de datos ligadas a este servicio (motor + puerto + rol).
  const [dbInstances, setDbInstances] = useState([]);
  const [addDep, setAddDep] = useState(false);
  const [depForm, setDepForm] = useState({ to_service_id: '', dep_type: 'depends_on', port: '', notes: '' });
  const [depSaving, setDepSaving] = useState(false);

  // ── Group memberships ──
  const [svcGroups, setSvcGroups] = useState([]);   // [{ group, member }]
  const [allGroups, setAllGroups] = useState([]);
  const [groupBusy, setGroupBusy] = useState(false);
  const [groupAddId, setGroupAddId] = useState('');

  // ── Load meta if not provided ──
  useEffect(() => {
    if (!companyId || (metaProp?.service_types?.length > 0)) return;
    api.getServicesMeta(companyId).then(m => { if (m.service_types) setMeta(m); }).catch(() => {});
  }, [companyId, metaProp]);

  // ── Populate form from service prop ──
  useEffect(() => {
    if (!service) { setForm(EMPTY); return; }
    setForm({
      name:               service.name || '',
      display_name:       service.display_name || '',
      service_type:       service.service_type || '',
      vendor:             service.vendor || '',
      version:            service.version || '',
      status:             service.status || 'running',
      criticality:        service.criticality || 'medium',
      hosting_type:       service.hosting_type || 'vm',
      host_ci_type:       service.host_ci_type || '',
      host_ci_id:         service.host_ci_id || '',
      host_server_id:     service.host_server_id || '',
      host_vm_id:         service.host_vm_id || '',
      ip_address_id:      service.ip_address_id || '',
      url:                service.url || '',
      port:               service.port || '',
      protocol:           service.protocol || 'tcp',
      description:        service.description || '',
      notes:              service.notes || '',
      tags:               Array.isArray(service.tags) ? service.tags.join(', ') : (service.tags || ''),
      owner_team:         service.owner_team || '',
      support_contact:    service.support_contact || '',
      sla_tier:           service.sla_tier || '',
      maintenance_window: service.maintenance_window || '',

    });
  }, [service]);

  // ── Load dependencies for existing services ──
  const loadDeps = useCallback(async () => {
    if (!service?.id || !companyId) return;
    try {
      const [d, all] = await Promise.all([
        api.getServiceDependencies(service.id, companyId),
        api.getServices(companyId),
      ]);
      setDeps(d || { outbound: [], inbound: [] });
      setRelations({
        out: d?.relations_out || [],
        in: d?.relations_in || [],
      });
      setAllSvcs(Array.isArray(all) ? all : (all.services || []));
    } catch (e) { console.error(e); }
  }, [service?.id, companyId]);

  // ── Elementos del diseño (con nombre y rol) ──
  const loadMembers = useCallback(async () => {
    if (!service?.id || !companyId) { setMembers([]); return; }
    try {
      const rows = await api.sdGetMembers(service.id, companyId);
      setMembers(Array.isArray(rows) ? rows : []);
    } catch (e) { console.error(e); setMembers([]); }
  }, [service?.id, companyId]);

  useEffect(() => { loadDeps(); loadMembers(); }, [loadDeps, loadMembers]);

  // ── Instancias de base de datos del servicio (módulo «Bases de Datos») ──
  const loadDbInstances = useCallback(async () => {
    if (!service?.id || !companyId) { setDbInstances([]); return; }
    try {
      const rows = await api.getDatabaseInstances(companyId, { service_id: service.id });
      setDbInstances(Array.isArray(rows) ? rows : []);
    } catch (e) { console.error(e); setDbInstances([]); }
  }, [service?.id, companyId]);

  useEffect(() => { loadDbInstances(); }, [loadDbInstances]);

  // ── Load group memberships for existing services ──
  const loadGroups = useCallback(async () => {
    if (!service?.id || !companyId) return;
    try {
      const res = await api.getServiceGroups(companyId);
      const groups = res.groups || [];
      setAllGroups(groups);
      const mine = [];
      groups.forEach(g => {
        const member = (g.members || []).find(m => m.service_id === service.id);
        if (member) mine.push({ group: g, member });
      });
      setSvcGroups(mine);
    } catch (e) { console.error(e); }
  }, [service?.id, companyId]);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const handleAddToGroup = async () => {
    if (!groupAddId) return;
    setGroupBusy(true);
    try {
      await api.addServiceGroupMember(Number(groupAddId), { service_id: service.id }, companyId);
      setGroupAddId('');
      await loadGroups();
    } catch (e) { setApiError(e.message); }
    finally { setGroupBusy(false); }
  };

  const handleRemoveFromGroup = async (groupId, memberId) => {
    setGroupBusy(true);
    try {
      await api.removeServiceGroupMember(groupId, memberId, companyId);
      await loadGroups();
    } catch (e) { setApiError(e.message); }
    finally { setGroupBusy(false); }
  };

  // ── Derived ──
  const currentStatus = statusColors[form.status] || statusColors.running;
  const currentCrit = critColors[form.criticality];
  const typeLabelMap = useMemo(() => {
    const m = {};
    (meta.service_types || []).forEach(t => { m[t.value] = t.label; });
    return m;
  }, [meta.service_types]);
  const hostingLabelMap = useMemo(() => {
    const m = {};
    (meta.hosting_types || []).forEach(h => { m[h.value] = h.label; });
    return m;
  }, [meta.hosting_types]);
  const depTypes = meta.dep_types || [
    { value: 'uses_db',          label: 'Usa base de datos de' },
    { value: 'proxied_by',       label: 'Proxied por' },
    { value: 'depends_on',       label: 'Depende de' },
    { value: 'uses_cache',       label: 'Usa caché de' },
    { value: 'uses_queue',       label: 'Usa cola de' },
    { value: 'replicates_to',    label: 'Replica hacia' },
    { value: 'backed_up_by',     label: 'Respaldado por' },
    { value: 'monitored_by',     label: 'Monitorizado por' },
    { value: 'authenticated_by', label: 'Autenticado por' },
    { value: 'resolves_via',     label: 'Resuelve vía' },
    { value: 'stores_to',        label: 'Almacena en' },
  ];
  const depTypeLabel = (v) => depTypes.find(d => d.value === v)?.label || v;

  const serviceLabelOf = (id, fallback) => {
    const svc = allSvcs.find(s => String(s.id) === String(id));
    return svc?.display_name || svc?.name || fallback;
  };
  const kindLabelOf = (kind, ciType) => REL_CI_LABELS[kind] || REL_CI_LABELS[ciType] || kind || ciType || 'Elemento';

  // Una sola lista de relaciones: las declaradas a mano y las del diseñador se
  // mezclan y se separan por su efecto real, no por la tabla donde viven.
  const needItems = [];
  const impactItems = [];

  (deps.outbound || []).forEach(d => needItems.push({
      key: `dep-out-${d.id}`,
      source: 'Servicio',
      icon: REL_CI_ICONS.service,
      relationLabel: depTypeLabel(d.dep_type),
      name: d.to_service_display_name || serviceLabelOf(d.to_service_id, d.to_service_name || `Servicio #${d.to_service_id}`),
      kindLabel: 'Servicio',
      serviceId: d.to_service_id,
      port: d.port,
      notes: d.notes,
      deletable: true,
      depId: d.id,
    }));
  (deps.inbound || []).forEach(d => impactItems.push({
      key: `dep-in-${d.id}`,
      source: 'Servicio',
      icon: REL_CI_ICONS.service,
      relationLabel: depTypeLabel(d.dep_type),
      name: d.from_service_display_name || serviceLabelOf(d.from_service_id, d.from_service_name || `Servicio #${d.from_service_id}`),
      kindLabel: 'Servicio',
      serviceId: d.from_service_id,
      port: d.port,
      notes: d.notes,
      deletable: false,
    }));

  const relationItem = (r, dir) => ({
      key: `rel-${dir}-${r.id}`,
      source: 'Diseñador',
      icon: REL_CI_ICONS[r.other_kind] || REL_CI_ICONS[r.other_ci_type] || REL_CI_ICONS.other,
      relationLabel: r.relation_label || r.relation || 'relación',
      name: r.other_name || kindLabelOf(r.other_kind, r.other_ci_type),
      kindLabel: kindLabelOf(r.other_kind, r.other_ci_type),
      serviceId: r.other_service_id,
      port: null,
      notes: null,
      deletable: false,
      relationId: r.id,
    });

  // Los componentes lógicos ya están en «Elementos del servicio»: no se
  // repiten aquí como si fueran dependencias externas.
  (relations.out || []).filter(r => r.other_ci_type !== 'logical_component').forEach(r => {
    const item = relationItem(r, 'out');
    (NEED_RELATION_NAMES.has(r.relation) ? needItems : impactItems).push(item);
  });
  // Todo lo que APUNTA al servicio (un servicio «tiene backup en» él, «usa»
  // él, «se autentica en» él…) describe a quién afecta si este servicio cae,
  // no algo que el servicio necesite para funcionar.
  (relations.in || []).filter(r => r.other_ci_type !== 'logical_component').forEach(r => {
    impactItems.push(relationItem(r, 'in'));
  });

  const totalLinks = needItems.length + impactItems.length;

  // ── Handlers ──
  const setField = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
    setApiError('');
    setErrors([]);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const handleDiscard = () => {
    if (service) {
      setForm({
        ...EMPTY,
        ...service,
        tags: Array.isArray(service.tags) ? service.tags.join(', ') : (service.tags || ''),
      });
    } else {
      onClose();
    }
    setIsDirty(false);
    setErrors([]);
    setApiError('');
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    setApiError('');
    try {
      const errs = [];
      if (!form.name.trim()) errs.push('El nombre es obligatorio');
      if (!form.service_type) errs.push('El tipo es obligatorio');
      if (errs.length) { setErrors(errs); setSaving(false); return; }

      const payload = { ...form };
      if (payload.host_ci_id === '') payload.host_ci_id = null;
      else if (payload.host_ci_id) payload.host_ci_id = Number(payload.host_ci_id);
      if (payload.host_server_id === '') payload.host_server_id = null;
      if (payload.host_vm_id === '') payload.host_vm_id = null;
      if (payload.ip_address_id === '') payload.ip_address_id = null;
      if (payload.port === '') payload.port = null;
      else payload.port = Number(payload.port) || null;
      if (payload.tags) payload.tags = payload.tags.split(',').map(t => t.trim()).filter(Boolean);
      else payload.tags = [];

      let created = null;
      if (isEditing) {
        await api.updateService(service.id, payload, companyId);
      } else {
        created = registroGuardado(await api.createService(payload, companyId));
        // Tras crear el servicio, pasar a modo edición y mostrar la pestaña de
        // accesos (columnas) sin tener que salir y volver a entrar.
        setActiveTab('access');
      }

      setIsDirty(false);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
      onSave?.(created);
    } catch (err) {
      setApiError(err.message || 'Error guardando servicio');
    } finally {
      setSaving(false);
    }
  };

  // ── Dependency handlers ──
  const handleAddDep = async (e) => {
    e.preventDefault();
    if (!depForm.to_service_id) return;
    setDepSaving(true);
    try {
      await api.createServiceDependency({
        from_service_id: service.id,
        to_service_id: parseInt(depForm.to_service_id),
        dep_type: depForm.dep_type,
        port: depForm.port || null,
        notes: depForm.notes || null,
      }, companyId);
      setAddDep(false);
      setDepForm({ to_service_id: '', dep_type: 'depends_on', port: '', notes: '' });
      loadDeps();
    } catch (e) { setApiError(e.message); }
    finally { setDepSaving(false); }
  };

  const handleDeleteDep = async (depId) => {
    try {
      await api.deleteServiceDependency(depId, companyId);
      loadDeps();
    } catch (e) { setApiError(e.message); }
  };

  // ── Datos guardados por los asistentes (Service.config_json) ──
  const assistantConfigBlocks = useMemo(
    () => visibleConfig(service?.config_json),
    [service?.config_json],
  );

  const hostingOptions = useMemo(() => {
    const base = meta.hosting_types?.length ? meta.hosting_types : HOSTING_FALLBACK;
    return base.map(h => ({
      ...h,
      ...(HOSTING_INFO[h.value] || {
        description: 'Tipo de alojamiento sin descripción.',
        Icon: QuestionMarkCircleIcon,
      }),
    }));
  }, [meta.hosting_types]);

  // ── Tabs ──
  const tabs = [
    ...(isEditing ? [{
      key: 'overview',
      label: (
        <>
          <MapIcon className="dv-tab-icon" />
          Vista general
        </>
      ),
    }] : []),
    { key: 'general', label: isCards ? 'Resumen' : 'Identificación' },
    ...(isEditing && assistantConfigBlocks.length
      ? [{ key: 'config', label: `🧾 Datos del asistente (${assistantConfigBlocks.length})` }]
      : []),
    { key: 'hosting', label: 'Alojamiento' },
    ...(isEditing ? [{ key: 'groups', label: `🗂️ Grupos (${svcGroups.length})` }] : []),
    ...(isEditing ? [{ key: 'deps', label: `Dependencias (${totalLinks})` }] : []),
    ...(isEditing ? [{ key: 'access', label: '🔗 Accesos' }] : []),
    ...(isEditing ? [{ key: 'docs', label: '📄 Documentación' }] : []),
    { key: 'org', label: 'SLA y Organización' },
    ...(isEditing && AUDITABLE_TYPES.includes(form.service_type) ? [{ key: 'audit', label: '🔍 Auditoría' }] : []),
    ...(isEditing && form.service_type === 'active_directory' ? [{ key: 'ad_topology', label: '🌐 Topología AD' }] : []),
    ...(isEditing && TOPOLOGY_SERVICE_TYPES.has(form.service_type) ? [{ key: 'topology', label: '🌐 Topología' }] : []),
  ];

  const paneClass = `dv-tab-pane${isCards ? ' sf2-pane' : ''}`;
  const tabsNav = (
    <div className="dv-tabs-nav">
      {tabs.map(tab => (
        <TabButton
          key={tab.key}
          type="button"
          className={`dv-tab-btn${activeTab === tab.key ? ' active' : ''}`}
          onClick={() => setActiveTab(tab.key)}
        >
          {tab.label}
        </TabButton>
      ))}
    </div>
  );

  return (
    <div className="dv-overlay">
      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={onClose}>
          <ArrowLeftIcon />
          Servicios
        </button>
        <DetailCrumb label={form.display_name || form.name} />
        <div className="dv-topbar-spacer" />

        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>✓ Guardado</span>
        )}
        {(apiError || errors.length > 0) && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ⚠ {apiError || errors[0]}
          </span>
        )}

        <div className="dv-topbar-actions">
          {isCards ? (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
              <XMarkIcon /> Cerrar
            </button>
          ) : (
          <>
          <span className="dv-status-badge" style={{ background: currentStatus.bg, color: currentStatus.color }}>
            {currentStatus.label}
          </span>
          {!editing ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon /> Cerrar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={() => setEditing(true)}>
                <CheckIcon /> Editar
              </button>
            </>
          ) : (
            <>
              {isDirty && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
                  <XMarkIcon />
                  Descartar
                </button>
              )}
              <button
                className="dv-btn dv-btn-primary"
                type="button"
                onClick={handleSubmit}
                disabled={saving || !isDirty}
              >
                {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('general')} title="Tipo de servicio">
          <span className="dv-smart-btn-icon">
            <WrenchScrewdriverIcon style={{ width: 18, height: 18, color: '#0369a1' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: '#0369a1', fontSize: '0.8rem' }}>
            {typeLabelMap[form.service_type] || form.service_type || 'Sin tipo'}
          </span>
          <span className="dv-smart-btn-label">Tipo</span>
        </button>

        {currentCrit && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('general')} title="Criticidad">
            <span className="dv-smart-btn-icon">
              <ShieldExclamationIcon style={{ width: 18, height: 18, color: currentCrit.color }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: currentCrit.color, fontSize: '0.85rem' }}>
              {currentCrit.label}
            </span>
            <span className="dv-smart-btn-label">Criticidad</span>
          </button>
        )}

        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('hosting')} title="Alojamiento">
          <span className="dv-smart-btn-icon">
            <ServerStackIcon style={{ width: 18, height: 18, color: '#7c3aed' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: '#7c3aed', fontSize: '0.8rem' }}>
            {hostingLabelMap[form.hosting_type] || form.hosting_type || '—'}
          </span>
          <span className="dv-smart-btn-label">Alojamiento</span>
        </button>

        {form.url && (
          <button className="dv-smart-btn" type="button" onClick={() => window.open(form.url, '_blank')} title="Abrir URL">
            <span className="dv-smart-btn-icon">
              <GlobeAltIcon style={{ width: 18, height: 18, color: '#059669' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#059669', fontSize: '0.7rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {form.url.replace(/^https?:\/\//, '').substring(0, 20)}
            </span>
            <span className="dv-smart-btn-label">URL</span>
          </button>
        )}

        {isEditing && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('deps')} title="Dependencias y relaciones del modelo">
            <span className="dv-smart-btn-icon">
              <LinkIcon style={{ width: 18, height: 18, color: '#ea580c' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#ea580c' }}>
              {totalLinks}
            </span>
            <span className="dv-smart-btn-label">Dependencias</span>
          </button>
        )}

        {isEditing && svcGroups.length > 0 && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('groups')} title="Grupos">
            <span className="dv-smart-btn-icon" style={{ fontSize: 18 }}>🗂️</span>
            <span className="dv-smart-btn-value" style={{ color: '#0d9488', fontSize: '0.8rem', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {svcGroups.map(sg => sg.group.name).join(', ')}
            </span>
            <span className="dv-smart-btn-label">Grupo{svcGroups.length !== 1 ? 's' : ''}</span>
          </button>
        )}

        {form.owner_team && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('org')} title="Equipo responsable">
            <span className="dv-smart-btn-icon">
              <UserGroupIcon style={{ width: 18, height: 18, color: '#6b7280' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#6b7280', fontSize: '0.8rem' }}>
              {form.owner_team}
            </span>
            <span className="dv-smart-btn-label">Equipo</span>
          </button>
        )}

        {form.sla_tier && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('org')} title="SLA">
            <span className="dv-smart-btn-icon">
              <ClockIcon style={{ width: 18, height: 18, color: '#059669' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#059669', fontSize: '0.8rem' }}>
              {form.sla_tier}
            </span>
            <span className="dv-smart-btn-label">SLA</span>
          </button>
        )}

        {isEditing && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('access')} title="Puntos de acceso">
            <span className="dv-smart-btn-icon">
              <GlobeAltIcon style={{ width: 18, height: 18, color: '#059669' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#059669', fontSize: '0.8rem' }}>
              {service?.access_point_count || accessPointCount || 0}
            </span>
            <span className="dv-smart-btn-label">Accesos</span>
          </button>
        )}

        {isEditing && (
          <DocSmartButton elementType="service" elementId={service.id} elementName={form.display_name || form.name} companyId={companyId} />
        )}

        {isEditing && (
          <ContactsSmartButton
            companyId={companyId}
            objectType="service"
            objectId={service.id}
            objectName={form.display_name || form.name}
          />
        )}
      </div>
      )}

      {/* ── Body ── (en lectura se apaga de golpe con el fieldset) ── */}
      <div className="dv-body">
        <div className={`dv-main${isCards ? ' sf2-main' : ''}`}>

        <FormCardsShell
          isCards={isCards}
          header={isCards ? (
            <FormCardsHead
              icon={<WrenchScrewdriverIcon style={{ width: 20, height: 20 }} />}
              title={form.display_name || form.name}
              onTitleChange={(v) => setForm(prev => ({ ...prev, display_name: v }))}
              placeholder="Nombre visible del servicio…"
              editing={editing}
              saving={saving}
              onSave={handleSubmit}
              onCancel={onClose}
              onEdit={() => setEditing(true)}
              crumb={[typeLabelMap[form.service_type] || form.service_type, currentCrit?.label].filter(Boolean).join(' › ')}
              badges={[
                { text: currentStatus.label, color: form.status === 'active' ? '#16a34a' : form.status === 'maintenance' ? '#f59e0b' : '#dc2626' },
                ...(currentCrit ? [{ text: currentCrit.label }] : []),
              ]}
              chips={[
                { value: svcGroups.length, label: 'grupos', onClick: () => setActiveTab('groups') },
                { value: totalLinks, label: 'dependencias', onClick: () => setActiveTab('deps') },
                { value: accessPointCount || service?.access_point_count || 0, label: 'accesos', onClick: () => setActiveTab('access') },
              ]}
              extra={isEditing ? (
                <DocSmartButton elementType="service" elementId={service.id} elementName={form.display_name || form.name} companyId={companyId} variant="chip" />
              ) : null}
            />
          ) : null}
          tabs={isCards ? tabsNav : null}
        >
        <fieldset className="dv-fieldset" disabled={!editing}>
        <form className="dv-form" onSubmit={handleSubmit}>
          {/* Record title */}
          <input
            className="dv-record-title"
            name="display_name"
            value={form.display_name}
            onChange={handleChange}
            placeholder="Nombre visible del servicio…"
          />

          {/* Tabs nav */}
          {!isCards && tabsNav}

          {/* ── Tab: Vista General ── */}
          {activeTab === 'overview' && isEditing && (
            <div className={paneClass}>
              <ServiceOverviewMap
                serviceId={service.id}
                companyId={companyId}
                service={service}
                onOpenService={onOpenService}
              />
            </div>
          )}

          {/* ── Tab: Grupos ── */}
          {activeTab === 'groups' && isEditing && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">🗂️ Grupos de este servicio</div>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: -4, marginBottom: 12 }}>
                  Un servicio puede pertenecer a varios grupos (p.ej. un motor Docker compartido por dos stacks).
                </p>

                {svcGroups.length === 0 ? (
                  <div style={{
                    padding: '18px', textAlign: 'center', borderRadius: 8,
                    border: '1px dashed var(--border)', color: 'var(--text-muted)',
                    fontSize: '0.85rem', marginBottom: 14,
                  }}>
                    Este servicio no pertenece a ningún grupo todavía.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                    {svcGroups.map(({ group, member }) => (
                      <div key={group.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px', borderRadius: 8,
                        border: '1px solid var(--border)',
                        borderLeft: '3px solid #0d9488',
                        background: 'var(--bg-card)',
                      }}>
                        <span style={{ fontSize: '1.4rem' }}>{group.icon}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                            {group.name}
                          </div>
                          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                            {group.group_type_label} · {group.member_count} servicio{group.member_count !== 1 ? 's' : ''}
                            {member.role && <> · rol: <strong>{member.role}</strong></>}
                          </div>
                        </div>
                        <button type="button"
                          onClick={() => handleRemoveFromGroup(group.id, member.id)}
                          disabled={groupBusy}
                          className="dv-btn dv-btn-secondary"
                          style={{ fontSize: '0.76rem', padding: '4px 10px' }}>
                          Quitar del grupo
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add to group */}
                {(() => {
                  const availableGroups = allGroups.filter(g => !svcGroups.find(sg => sg.group.id === g.id));
                  if (availableGroups.length === 0) {
                    return (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        {allGroups.length === 0
                          ? 'No hay grupos creados. Crea uno desde la lista de servicios («Nuevo grupo»).'
                          : 'Ya pertenece a todos los grupos existentes.'}
                      </div>
                    );
                  }
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                        Añadir a grupo:
                      </span>
                      <select value={groupAddId} onChange={e => setGroupAddId(e.target.value)}
                        className="dv-input" style={{ maxWidth: 260, flex: 1 }}>
                        <option value="">Selecciona un grupo…</option>
                        {availableGroups.map(g => (
                          <option key={g.id} value={g.id}>{g.icon} {g.name}</option>
                        ))}
                      </select>
                      <button type="button" onClick={handleAddToGroup}
                        disabled={!groupAddId || groupBusy}
                        className="dv-btn dv-btn-primary" style={{ fontSize: '0.8rem' }}>
                        Añadir
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* ── Tab: Identificación ── */}
          {activeTab === 'general' && (
            <div className={paneClass}>
              {isCards && (
                <div className="sf2-hl" style={{ marginBottom: 12 }}>
                  <div className="sf2-kpi"><span>Tipo</span><b style={{ fontSize: '0.95rem' }}>{typeLabelMap[form.service_type] || form.service_type || '—'}</b></div>
                  <div className="sf2-kpi"><span>Criticidad</span><b style={{ fontSize: '0.95rem' }}>{currentCrit?.label || '—'}</b></div>
                  <div className="sf2-kpi"><span>Grupos</span><b>{svcGroups.length}</b></div>
                  <div className="sf2-kpi"><span>Dependencias</span><b>{totalLinks}</b></div>
                  <div className="sf2-kpi"><span>Estado</span><b style={{ fontSize: '0.95rem' }}>{currentStatus.label}</b></div>
                </div>
              )}
              <div className="dv-section">
                <div className="dv-section-title">Datos del servicio</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Nombre interno <span className="text-danger">*</span></label>
                    <input name="name" value={form.name} onChange={handleChange} className="form-control" placeholder="odoo, haproxy, postgres..." required autoFocus={!isEditing} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Nombre visible</label>
                    <input name="display_name" value={form.display_name} onChange={handleChange} className="form-control" placeholder="Odoo ERP, HAProxy LB..." />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Tipo <span className="text-danger">*</span></label>
                    <select name="service_type" value={form.service_type} onChange={handleChange} className="form-select" required>
                      <option value="">— seleccionar —</option>
                      {(meta.service_types || []).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Estado</label>
                    <select name="status" value={form.status} onChange={handleChange} className="form-select">
                      {(meta.statuses || []).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Criticidad</label>
                    <select name="criticality" value={form.criticality} onChange={handleChange} className="form-select">
                      {(meta.criticalities || []).map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Vendor / Fabricante</label>
                    <input name="vendor" value={form.vendor} onChange={handleChange} className="form-control" placeholder="Odoo S.A., HashiCorp..." />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Versión</label>
                    <input name="version" value={form.version} onChange={handleChange} className="form-control" placeholder="17.0, 2.8.1..." />
                  </div>
                  <div className="col-md-8">
                    <label className="form-label">Descripción</label>
                    <textarea name="description" value={form.description} onChange={handleChange} className="form-control" rows={2} placeholder="Breve descripción del servicio..." />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Alojamiento ── */}
          {activeTab === 'hosting' && (
            <div className={paneClass}>
              <div className="sf-hosting-hero">
                <div className="sf-hosting-hero-icon"><ServerStackIcon /></div>
                <div>
                  <div className="sf-hosting-hero-title">Dónde se ejecuta este servicio</div>
                  <p>
                    Elige el tipo de alojamiento y asocia los servidores o máquinas que lo
                    soportan. La Vista general y las dependencias usan esta información.
                  </p>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Tipo de alojamiento</div>
                <div className="sf-hosting-grid" role="radiogroup" aria-label="Tipo de alojamiento">
                  {hostingOptions.map(({ value, label, description, Icon }) => {
                    const selected = form.hosting_type === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={`sf-hosting-card${selected ? ' active' : ''}`}
                        onClick={() => setField('hosting_type', value)}
                      >
                        <span className="sf-hosting-card-icon"><Icon /></span>
                        <span className="sf-hosting-card-body">
                          <span className="sf-hosting-card-label">{label}</span>
                          <span className="sf-hosting-card-desc">{description}</span>
                        </span>
                        {selected && <CheckIcon className="sf-hosting-card-check" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {isEditing && (
                <>
                  {!['saas', 'paas'].includes(form.hosting_type) ? (
                    <div className="dv-section">
                      <div className="dv-section-title">Equipos del servicio</div>
                      <p className="sf-hosting-sub">
                        Servidores, VMs o dispositivos que forman este servicio.
                      </p>
                      <ServiceHostsSection
                        serviceId={service.id}
                        companyId={companyId}
                        hostingType={form.hosting_type}
                        editable={editing}
                      />
                    </div>
                  ) : (
                    <div className="dv-section">
                      <div className="dv-section-title">Equipos del servicio</div>
                      <div className="sf-hosting-external">
                        <GlobeAltIcon />
                        <div>
                          <strong>Servicio externo</strong>
                          <span>
                            {form.hosting_type === 'saas' ? 'SaaS' : 'PaaS'}: no se aloja en
                            equipos de tu inventario.
                          </span>
                        </div>
                        <TabButton
                          type="button"
                          className="dv-btn dv-btn-secondary"
                          onClick={() => setActiveTab('deps')}
                        >
                          <LinkIcon /> Ver dependencias
                        </TabButton>
                      </div>
                    </div>
                  )}

                  <div className="sf-hosting-related">
                    <TabButton
                      type="button"
                      className="sf-hosting-related-card"
                      onClick={() => setActiveTab('access')}
                    >
                      <GlobeAltIcon />
                      <span>
                        <strong>Accesos y URLs</strong>
                        <small>SSH, RDP, HTTPS y otros métodos.</small>
                      </span>
                    </TabButton>
                    <TabButton
                      type="button"
                      className="sf-hosting-related-card"
                      onClick={() => setActiveTab('deps')}
                    >
                      <LinkIcon />
                      <span>
                        <strong>Dependencias</strong>
                        <small>Qué necesita y qué depende de este servicio.</small>
                      </span>
                    </TabButton>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Tab: Datos del asistente ── */}
          {activeTab === 'config' && isEditing && assistantConfigBlocks.length > 0 && (
            <div className={paneClass}>
              <ServiceConfigView config={service?.config_json} />
            </div>
          )}

          {/* ── Tab: SLA y Organización ── */}
          {activeTab === 'org' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Responsabilidad</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Equipo responsable</label>
                    <input name="owner_team" value={form.owner_team} onChange={handleChange} className="form-control" placeholder="Sistemas, DevOps..." />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Contacto de soporte</label>
                    <input name="support_contact" value={form.support_contact} onChange={handleChange} className="form-control" placeholder="soporte@empresa.com" />
                  </div>
                </div>
              </div>
              <div className="dv-section">
                <div className="dv-section-title">SLA y Mantenimiento</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Nivel SLA</label>
                    <input name="sla_tier" value={form.sla_tier} onChange={handleChange} className="form-control" placeholder="Gold, Silver, Best-effort..." />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Ventana de mantenimiento</label>
                    <input name="maintenance_window" value={form.maintenance_window} onChange={handleChange} className="form-control" placeholder="Dom 02:00-04:00 UTC" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Tags <span className="text-muted" style={{ fontSize: '0.75rem' }}>(coma)</span></label>
                    <input name="tags" value={form.tags} onChange={handleChange} className="form-control" placeholder="producción, erp, crítico" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Dependencias ── */}
          {activeTab === 'deps' && isEditing && (
            <div className={paneClass}>
              {/* ── Elementos del servicio (miembros del Diseñador) ── */}
              <div className="dv-section" id="sf-elementos-servicio">
                <div className="dv-section-title">🧩 Elementos del servicio ({members.length})</div>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 12px' }}>
                  Los elementos (equipos, servicios, subredes, volúmenes...) que forman este
                  servicio en el modelo, con el papel que juega cada uno. Son las cajas que ves
                  en el Diseñador.
                </p>
                {members.length === 0 ? (
                  <div className="dv-empty-state">
                    Este servicio no tiene elementos en el modelo. Añádelos en el Diseñador
                    (o créalo con un asistente, que los añade solo).
                  </div>
                ) : (
                  <div className="sf-el-list">
                    {members.map(m => (
                      <div key={`m-${m.id}`} className="sf-el-row">
                        <span className="sf-rel-icon">
                          {m.is_logical
                            ? (m.logical_icon || '🗂️')
                            : (REL_CI_ICONS[m.kind] || REL_CI_ICONS[m.ci_type] || '📦')}
                        </span>
                        <span className="sf-el-name">
                          {m.ci_type === 'service' && !m.is_logical ? (
                            <button
                              type="button"
                              className="sf-rel-link"
                              onClick={() => onOpenService?.(m.ci_id)}
                              title="Abrir la ficha de este servicio"
                            >
                              {m.name} ↗
                            </button>
                          ) : (
                            <span className="sf-rel-plain">{m.name}</span>
                          )}
                          {m.is_logical && m.logical_detail && (
                            <span className="sf-el-detail">{m.logical_detail}</span>
                          )}
                        </span>
                        {m.role && <span className="sf-el-role">{m.role}</span>}
                        {!m.is_logical && (
                          <span className="sf-rel-kind">{m.kind_label || m.kind || m.ci_type}</span>
                        )}
                        {m.is_logical && <span className="sf-rel-kind">grupo lógico</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Instancias de base de datos del servicio ── */}
              {dbInstances.length > 0 && (
                <div className="dv-section" id="sf-bases-de-datos">
                  <div className="dv-section-title">🗄️ Bases de datos ({dbInstances.length})</div>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 12px' }}>
                    Instancias (motor + puerto) ligadas a este servicio. Las copias indican de qué
                    instancia salen. Se gestionan en «Bases de Datos».
                  </p>
                  <div className="sf-el-list">
                    {dbInstances.map((d) => (
                      <div key={`db-${d.id}`} className="sf-el-row">
                        <span className="sf-rel-icon">🗄️</span>
                        <span className="sf-el-name">
                          <span className="sf-rel-plain">
                            {d.name || `${d.dbms || 'BD'} ${d.version || ''}`.trim()}
                            {' · '}{d.dbms}{d.version ? ` ${d.version}` : ''}
                            {d.port ? ` · puerto ${d.port}` : ''}
                          </span>
                          {d.copy_of && (
                            <span className="sf-el-detail">
                              copia de {d.copy_of}
                              {COPY_METHOD_LABELS[d.copy_method] ? ` · ${COPY_METHOD_LABELS[d.copy_method]}` : ''}
                            </span>
                          )}
                          {!d.copy_of && d.copy_method && (
                            <span className="sf-el-detail">{COPY_METHOD_LABELS[d.copy_method] || d.copy_method}</span>
                          )}
                        </span>
                        {d.role && <span className="sf-el-role">{DB_ROLE_LABELS[d.role] || d.role}</span>}
                        <span className="sf-rel-kind">{d.host_vm_name || d.host_server_name || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Vista unificada: necesita vs impacto ── */}
              <div className="sf-dep-head">
                <div>
                  <div className="dv-section-title" style={{ margin: 0, border: 'none', paddingBottom: 0 }}>
                    Dependencias del servicio
                  </div>
                  <p className="sf-dep-lead">
                    Qué necesita este servicio para funcionar y qué queda afectado si falla.
                    Se combinan las relaciones del diseñador y las dependencias entre servicios.
                  </p>
                </div>
                <div className="sf-dep-head-actions">
                  <TabButton
                    type="button"
                    className="dv-btn dv-btn-secondary"
                    onClick={() => { if (!editing) setEditing(true); setAddDep(v => !v); }}
                  >
                    + {addDep ? 'Cancelar' : 'Añadir servicio'}
                  </TabButton>
                  <a
                    className="dv-btn dv-btn-secondary"
                    style={{ textDecoration: 'none' }}
                    href={`/services/designer?service=${service.id}`}
                  >
                    Abrir diseñador ↗
                  </a>
                </div>
              </div>

              <div className="sf-dep-summary">
                <div className="sf-dep-summary-card sf-dep-summary-need">
                  <span>Necesita</span>
                  <strong>{needItems.length}</strong>
                </div>
                <div className="sf-dep-summary-card sf-dep-summary-impact">
                  <span>Impacto</span>
                  <strong>{impactItems.length}</strong>
                </div>
                <div className="sf-dep-summary-card">
                  <span>Elementos</span>
                  <strong>{members.length}</strong>
                </div>
              </div>

              {addDep && (
                <div className="sf-dep-form">
                  <div className="sf-dep-form-head">
                    <div>
                      <strong>Añadir dependencia de servicio</strong>
                      <span>Para equipos, grupos o relaciones del modelo usa el diseñador.</span>
                    </div>
                    <button type="button" className="sf-host-form-close"
                      onClick={() => setAddDep(false)} title="Cerrar">
                      <XMarkIcon />
                    </button>
                  </div>
                  <div className="sf-dep-form-grid">
                    <label className="sf-host-field">
                      <span>Tipo de dependencia</span>
                      <select className="form-select form-select-sm"
                        value={depForm.dep_type}
                        onChange={e => setDepForm(f => ({ ...f, dep_type: e.target.value }))}>
                        {depTypes.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                    </label>
                    <label className="sf-host-field">
                      <span>Servicio del que depende</span>
                      <select className="form-select form-select-sm"
                        value={depForm.to_service_id}
                        onChange={e => setDepForm(f => ({ ...f, to_service_id: e.target.value }))}
                        required>
                        <option value="">— elegir servicio —</option>
                        {allSvcs.filter(s => s.id !== service.id).map(s => (
                          <option key={s.id} value={s.id}>{s.display_name || s.name}</option>
                        ))}
                      </select>
                    </label>
                    <label className="sf-host-field">
                      <span>Puerto (opcional)</span>
                      <input type="number" className="form-control form-control-sm"
                        placeholder="5432" value={depForm.port}
                        onChange={e => setDepForm(f => ({ ...f, port: e.target.value }))} />
                    </label>
                  </div>
                  <label className="sf-host-field sf-dep-form-notes">
                    <span>Notas (opcional)</span>
                    <input className="form-control form-control-sm"
                      placeholder="Qué se usa, para qué…" value={depForm.notes}
                      onChange={e => setDepForm(f => ({ ...f, notes: e.target.value }))} />
                  </label>
                  <div className="sf-dep-form-actions">
                    <button type="button" className="dv-btn dv-btn-primary"
                      onClick={handleAddDep} disabled={depSaving || !depForm.to_service_id}>
                      {depSaving ? 'Añadiendo…' : 'Añadir dependencia'}
                    </button>
                    <button type="button" className="dv-btn dv-btn-secondary"
                      onClick={() => setAddDep(false)}>Cancelar</button>
                  </div>
                </div>
              )}

              <div className="sf-dep-columns">
                <section className="sf-dep-column sf-dep-column-need">
                  <div className="sf-dep-col-head">
                    <span className="sf-dep-col-dot" />
                    <strong>Necesita</strong>
                    <span className="sf-dep-col-count">{needItems.length}</span>
                  </div>
                  <p className="sf-dep-col-sub">Si falla alguno, el servicio puede dejar de funcionar.</p>
                  {needItems.length === 0 ? (
                    <div className="sf-dep-empty">
                      Sin dependencias registradas. Añade un servicio o crea la relación en el diseñador.
                    </div>
                  ) : needItems.map(item => (
                    <DependencyRow
                      key={item.key}
                      item={item}
                      onOpenService={onOpenService}
                      canDelete={editing}
                      onDelete={handleDeleteDep}
                    />
                  ))}
                </section>

                <section className="sf-dep-column sf-dep-column-impact">
                  <div className="sf-dep-col-head">
                    <span className="sf-dep-col-dot" />
                    <strong>Impacto</strong>
                    <span className="sf-dep-col-count">{impactItems.length}</span>
                  </div>
                  <p className="sf-dep-col-sub">Qué servicios o elementos se ven afectados si este cae.</p>
                  {impactItems.length === 0 ? (
                    <div className="sf-dep-empty">
                      Nada depende de este servicio todavía.
                    </div>
                  ) : impactItems.map(item => (
                    <DependencyRow
                      key={item.key}
                      item={item}
                      onOpenService={onOpenService}
                      canDelete={editing}
                      onDelete={handleDeleteDep}
                    />
                  ))}
                </section>
              </div>

              <p className="sf-dep-footnote">
                «Servicio» = dependencia entre servicios; «Diseñador» = relación creada en el canvas
                (equipos, servicios o grupos lógicos). Las relaciones del diseñador se editan allí.
              </p>
            </div>
          )}
          {/* ── Tab: Auditoría AD ── */}
          {activeTab === 'audit' && isEditing && AUDITABLE_TYPES.includes(form.service_type) && (
            <div className={paneClass}>
              <ServiceAuditTab serviceId={service?.id} companyId={companyId} />
            </div>
          )}
          {/* ── Tab: Topología AD ── */}
          {activeTab === 'ad_topology' && isEditing && form.service_type === 'active_directory' && (
            <ADTopologyView serviceId={service?.id} companyId={companyId} />
          )}
          {/* ── Tab: Topología (genérica por tipo) ── */}
          {activeTab === 'topology' && isEditing && TOPOLOGY_SERVICE_TYPES.has(form.service_type) && (
            <ServiceTopologyView
              serviceType={form.service_type}
              serviceId={service?.id}
              companyId={companyId}
              serviceName={form.display_name || form.name}
            />
          )}

          {/* ── Tab: Accesos ── */}
          {activeTab === 'access' && isEditing && (
            <div className={paneClass}>
              <ServiceAccessPointsTab
                serviceId={service.id}
                companyId={companyId}
                onCountChange={setAccessPointCount}
              />
            </div>
          )}

          {/* ── Tab: Documentación ── */}
          {activeTab === 'docs' && isEditing && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Documentación vinculada</div>
                <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 12 }}>
                  Usa el botón 📄 de la barra superior para buscar, crear o vincular documentos a este servicio.
                </p>
                <DocsInlineList
                  elementType="service"
                  elementId={service.id}
                  elementName={form.display_name || form.name}
                  companyId={companyId}
                />
              </div>
            </div>
          )}
        </form>
        </fieldset>
        <FormDirtyDock
          show={isCards && isDirty}
          saving={saving}
          onDiscard={handleDiscard}
          onSave={() => handleSubmit(null)}
        />
        </FormCardsShell>
        </div>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="service"
          ciId={service?.id}
          tipoLegible="el servicio"
        />
      </div>
    </div>
  );
}
